import { useIntl } from 'react-intl';
import { CRow, CCol, CCard, CCardHeader, CCardBody } from '@coreui/react';
import DataTable from 'components/common/DataTable';
import * as yup from 'yup';
import { emailInvalid, nullTypeError, translatedMessage } from 'utils/yup';
import { Formik, Form, Field, type FormikHelpers } from 'formik';
import TextInput from 'components/inputs/TextInput';
import Button from 'components/common/Button';
import s from './AdminsTable.module.scss';

export interface IAdminFormValues {
  email: string;
}

/** A row of the admins table. */
export interface IAdminRow {
  name: string;
  email: string | null;
}

export interface ITableField {
  key: string;
  label: string;
}

interface IAdminsTableProps {
  assignAdmin: (
    values: IAdminFormValues,
    formikActions: FormikHelpers<IAdminFormValues>,
  ) => Promise<void>;
  unassignAdmin: (admin: IAdminRow) => Promise<void>;
  admins: IAdminRow[];
  fields: ITableField[];
}

const initialValue: IAdminFormValues = {
  email: '',
};

const schema = yup.object().shape({
  email: yup
    .string()
    .required(translatedMessage('errors.email_required'))
    .email(emailInvalid)
    .nonNullable(nullTypeError('string')),
});

const AdminsTable = ({ assignAdmin, unassignAdmin, admins, fields }: IAdminsTableProps) => {
  const { formatMessage: t } = useIntl();
  return (
    <CRow>
      <CCol md={12} xl={8} className="mx-auto">
        <CCard>
          <CCardHeader>{t({ id: 'admins.title' })}</CCardHeader>

          <Formik initialValues={initialValue} validationSchema={schema} onSubmit={assignAdmin}>
            {({ isSubmitting }) => (
              <Form>
                <div className={s.emailField}>
                  <Field
                    type="email"
                    name="email"
                    component={TextInput}
                    required
                    label={t({ id: 'auth.email' })}
                  />
                  <Button
                    disabled={isSubmitting}
                    loading={isSubmitting}
                    color="primary"
                    type="submit"
                  >
                    {t({ id: 'admins.add_admin_btn' })}
                  </Button>
                </div>
              </Form>
            )}
          </Formik>

          <CCardBody>
            <DataTable
              items={admins}
              fields={fields}
              addTableClasses={s.adminsTable}
              scopedSlots={{
                unassign: ({ name, email }: IAdminRow) => (
                  <td>
                    <Button
                      color="danger"
                      onClick={() => unassignAdmin({ email, name })}
                      disabled={admins.length === 1}
                    >
                      {t({ id: 'admins.unassign' })}
                    </Button>
                  </td>
                ),
              }}
            />
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>
  );
};

export default AdminsTable;
